import { quotaLevel } from '../../../engine/services/quota-state.js';
import { getStorageInfo, formatBytes } from '../../../engine/services/storage-service.js';
import { listAttachments } from '../../../engine/services/attachment-service.js';
import { readDocSize, listStoredAttachments } from '../../../engine/storage/index.js';
import { isOnlineSlotEnabled } from '../../../engine/crypto/vault-storage.js';
import { createQuotaBar } from './quota-bar.js';
import { t } from '../../../shared/i18n/index.js';
import { mapSizeInfo } from '../../../engine/services/map-size.js';

export function createStorageSummary(info) {
  const section = document.createElement('div');
  section.className = 'storage-usage-summary';
  const total = document.createElement('strong');
  total.textContent = t(info.quotaScope === 'attachments' ? 'vault.storage.attachmentTitle' : 'vault.storage.vaultTitle');
  const note = document.createElement('small');
  note.textContent = t(info.quotaScope === 'attachments' ? 'vault.storage.attachmentNote' : 'vault.storage.vaultNote');
  note.title = t('vault.storage.noteTitle');
  const used = info.quotaScope === 'combined' ? info.totalBytes : info.storageUsed;
  section.append(total, note, createQuotaBar(used, info.storageQuota));
  if (info.pendingRecoveryBytes > 0) {
    // Snapshots of maps this user was removed from: their data until recovered.
    const pending = document.createElement('small');
    pending.className = 'storage-pending-recovery';
    const count = info.pendingRecoveries.length;
    pending.textContent = t('vault.storage.pendingRecovery', { size: formatBytes(info.pendingRecoveryBytes), count });
    pending.title = t('vault.storage.pendingRecoveryTitle');
    section.appendChild(pending);
  }
  const level = quotaLevel(used, info.storageQuota);
  if (['warning', 'near', 'over'].includes(level)) {
    const warning = document.createElement('p');
    warning.className = 'storage-quota-warning';
    warning.setAttribute('role', 'status');
    warning.textContent = t(level === 'over' ? 'vault.storage.over' : 'vault.storage.near');
    section.appendChild(warning);
  }
  return section;
}

/** Batched server metadata, plus read-only local measurements. No document
 * content is downloaded merely to display its server size. */
export async function updateDocumentSizes(container, vault) {
  const targets = [...container.querySelectorAll('[data-storage-doc-id]')];
  if (!targets.length) return;
  let info = null;
  if (isOnlineSlotEnabled()) {
    try { info = await getStorageInfo(); } catch { /* Show unavailable, never a false zero. */ }
  }
  await Promise.all(targets.map(async target => {
    const id = target.dataset.storageDocId;
    try {
      const doc = vault?.getDoc(id);
      let text, title, count = 0;
      if (doc?._storage === 'local') {
        // Content plus the attachments kept on this device.
        const bytes = await readDocSize(id);
        let files = [];
        try { files = await listStoredAttachments('device', id); } catch { /* none readable */ }
        count = files.length;
        const fileBytes = files.reduce((sum, file) => sum + (file.size || 0), 0);
        if (bytes === null && !count) text = t('vault.size.localUnavailable');
        else if (target.hasAttribute('data-size-detail') && count) {
          text = `${t('vault.size.detail', { map: formatBytes(bytes || 0), attachments: formatBytes(fileBytes) })} · ${t('vault.size.onDevice', { size: formatBytes((bytes || 0) + fileBytes) })}`;
        } else text = t('vault.size.onDevice', { size: formatBytes((bytes || 0) + fileBytes) });
        title = t('vault.size.onDeviceTitle');
      } else {
        let size = info?.documents.get(id);
        if (!size && info && !info.documents.size && info.mapBytes === null) {
          // Compatibility with servers that only expose attachment usage.
          const files = await listAttachments(id);
          size = { mapBytes: null, attachmentBytes: files.reduce((sum, file) => sum + file.sizeOriginal, 0), attachmentCount: files.length };
        }
        if (!size) { text = t('vault.size.unavailable'); title = t('vault.size.unavailableTitle'); }
        else {
          count = size.attachmentCount;
          text = target.hasAttribute('data-size-detail')
            ? t('vault.size.detail', { map: formatBytes(size.mapBytes), attachments: formatBytes(size.attachmentBytes) })
            : size.mapBytes === null ? t('vault.size.attachmentsOnly', { size: formatBytes(size.attachmentBytes) })
            : formatBytes(size.mapBytes + size.attachmentBytes);
          title = t('vault.size.serverTitle') +
            (size.role && size.role !== 'owner' ? ` ${t('vault.size.sharedTitle')}` : '');
        }
      }
      // From 80% of the size limit (an open map, measured on this device).
      const fill = mapSizeInfo(id);
      if (fill && fill.level !== 'normal') text = `${text} · ${t('vault.size.percentFull', { percent: fill.percent })}`;
      if (!target.isConnected) return;
      target.textContent = text; target.title = title;
      target.closest('.vault-map')?.querySelector('.vault-map-expand')?.classList.toggle('has-attachments', count > 0);
    } catch {
      if (target.isConnected) target.textContent = t('vault.size.unavailable');
    }
  }));
}

export async function loadStorageSummary(container) {
  if (!container || !isOnlineSlotEnabled()) return;
  try {
    const info = await getStorageInfo();
    if (container.isConnected) container.replaceChildren(createStorageSummary(info));
  } catch {
    if (container.isConnected) container.textContent = t('vault.storage.serverUnavailable');
  }
}
